// 钻石钱包 / 订单 / 收藏 / 任务 / 作品接口（原 apps/customer/src/business/api.ts 的 JS 版本）。
import { AuthError, httpTransport, isRecord, isUuid as uuid, validateOrigin } from './http'
import { parsePayment } from './wechat-payment'

function invalid() {
  throw new AuthError('INVALID_RESPONSE')
}

const object = value => { if (!isRecord(value)) invalid(); return value }

const integer = (value) =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : invalid()

const fields = {
  orders: ['id', 'packId', 'amountFen', 'diamonds', 'status', 'createdAt', 'expiresAt'],
  entries: [
    'id',
    'referenceId',
    'kind',
    'availableDelta',
    'heldDelta',
    'availableAfter',
    'heldAfter',
    'createdAt',
  ],
  favorites: ['templateId', 'title', 'image', 'available', 'createdAt'],
  jobs: [
    'id',
    'kind',
    'state',
    'role',
    'cost',
    'parentWorkId',
    'attempts',
    'errorCode',
    'createdAt',
    'updatedAt',
  ],
  works: ['id', 'kind', 'role', 'parentWorkId', 'width', 'height', 'createdAt'],
}


function parseRows(kind, rows) {
  return rows.map((value) => {
        const x = object(value)
        const row = {}
        for (const key of fields[kind]) {
          const v = x[key]
          if (v !== null && typeof v !== 'string' && typeof v !== 'number' && typeof v !== 'boolean')
            invalid()
          if ((typeof v === 'number' && !Number.isSafeInteger(v)) || (typeof v === 'string' && v.length > 2048))
            invalid()
          row[key] = v
        }
        if (kind === 'jobs') {
          if (x.runningHubTaskId !== undefined) {
            if (x.runningHubTaskId !== null && (typeof x.runningHubTaskId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(x.runningHubTaskId))) invalid()
            row.runningHubTaskId = x.runningHubTaskId
          }
          if (x.templateId !== undefined) {
            if (x.templateId !== null && (typeof x.templateId !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(x.templateId))) invalid()
            row.templateId = x.templateId
          }
          for (const key of ['retryReleased', 'retryAllowed']) {
            if (x[key] !== undefined && typeof x[key] !== 'boolean') invalid()
            if (x[key] !== undefined) row[key] = x[key]
          }
        }
        if (kind === 'works' && x.jobId !== undefined) {
          if (x.jobId !== null && !uuid(x.jobId)) invalid()
          row.jobId = x.jobId
        }
        return row
      })
}

export function createBusinessApi(base, development = false, transport = httpTransport) {
  async function request(token, path, data, binary = false) {
    const response = await transport({
      url: validateOrigin(base, development) + '/v1/business/' + path,
      method: data ? 'POST' : 'GET',
      data,
      responseType: binary ? 'arraybuffer' : 'json',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
    if (response.status !== 200) {
      const code =
        response.data && typeof response.data === 'object' ? response.data.code : null
      throw new AuthError(
        typeof code === 'string' && /^[A-Z_]{1,64}$/.test(code) ? code : 'BUSINESS_UNAVAILABLE',
        response.status,
      )
    }
    return response.data
  }

  return {
    async jobInputs(token, id) {
      if (!uuid(id)) throw new AuthError('INVALID_REQUEST')
      const x = object(await request(token, `jobs/${id}/inputs`))
      for (const key of ['malePhotoId', 'femalePhotoId', 'maleModelId', 'femaleModelId']) if (x[key] !== null && !uuid(x[key])) invalid()
      if (x.templateId !== null && (typeof x.templateId !== 'string' || !/^[A-Za-z0-9_-]{1,64}$/.test(x.templateId))) invalid()
      if (x.templateTitle !== null && (typeof x.templateTitle !== 'string' || x.templateTitle.length > 120)) invalid()
      if (x.templateImage !== null && (typeof x.templateImage !== 'string' || x.templateImage.length > 2048 || !/^https:\/\//.test(x.templateImage))) invalid()
      if (x.templateImage) { const u = new URL(x.templateImage); if (u.username || u.password || u.hash) invalid() }
      const measurements = object(x.measurements)
      for (const [key, value] of Object.entries(measurements)) if (!['maleHeightCm','maleWeightKg','femaleHeightCm','femaleWeightKg'].includes(key) || !Number.isInteger(value) || value < (key.endsWith('HeightCm') ? 100 : 30) || value > 250) invalid()
      const customization = object(x.customization || {})
      for (const [key, value] of Object.entries(customization)) {
        if (key === 'beautyEnabled') { if (typeof value !== 'boolean') invalid() }
        else if (!['hairstyle', 'specialRequests'].includes(key) || typeof value !== 'string' || Array.from(value).length > (key === 'hairstyle' ? 300 : 600) || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(value)) invalid()
      }
      const references = x.referencePhotoIds || []
      if (!Array.isArray(references) || references.length > 2 || references.some(id => !uuid(id)) || new Set(references).size !== references.length) invalid()
      x.referencePhotoIds = references
      x.customization = customization
      return x
    },
    async thumbnail(token, id) { if (!uuid(id)) throw new AuthError('INVALID_REQUEST'); const bytes = await request(token, `works/${id}/thumbnail`, undefined, true); if (!(bytes instanceof ArrayBuffer) || bytes.byteLength > 33554432) invalid(); return bytes },
    async capabilities(token) {
      const x = object(await request(token, 'capabilities'))
      if (typeof x.paymentsEnabled !== 'boolean' || !Array.isArray(x.packs)) invalid()
      const modes = x.paymentModes || { NATIVE: x.paymentsEnabled, H5: false, JSAPI: false }
      if (['NATIVE', 'H5', 'JSAPI'].some(k => typeof modes[k] !== 'boolean')) invalid()
      if (x.alipayEnabled !== undefined && typeof x.alipayEnabled !== 'boolean') invalid()
      return { alipayEnabled: x.alipayEnabled === true, paymentModes: modes, paymentsEnabled: x.paymentsEnabled, generation: object(x.generation), packs: x.packs.map(p => ({ id: String(p.id), amountFen: integer(p.amountFen), diamonds: integer(p.diamonds) })) }
    },
    async prepay(token, operationId, packId, mode = 'NATIVE') {
      if (!uuid(operationId)) throw new AuthError('INVALID_REQUEST')
      return parsePayment(await request(token, mode === 'ALIPAY' ? 'payments/alipay' : 'payments/wechat', mode === 'ALIPAY' ? { operationId, packId } : { operationId, packId, mode }))
    },
    async resumePayment(token, id, mode = 'NATIVE') {
      if (!uuid(id)) throw new AuthError('INVALID_REQUEST')
      return parsePayment(await request(token, `orders/${id}/${mode === 'ALIPAY' ? 'alipay' : 'payment'}`, mode === 'ALIPAY' ? {} : { mode }), id)
    },
    async closeOrder(token, id) {
      if (!uuid(id)) throw new AuthError('INVALID_REQUEST')
      const result = object(await request(token, `orders/${id}/close`, {}))
      if (result.id !== id || result.status !== 'CLOSED') invalid()
      return result
    },
    async payment(token, id) {
      if (!uuid(id)) throw new AuthError('INVALID_REQUEST')
      const x = object(await request(token, `orders/${id}/payment`))
      if (x.id !== id || !['PENDING', 'PAID', 'CLOSED'].includes(x.status)) invalid()
      return x
    },
    async generate(token, mode, data) {
      if (!['models', 'weddings'].includes(mode) || !uuid(data.operationId)) throw new AuthError('INVALID_REQUEST')
      return object(await request(token, mode, data))
    },
    async content(token, id) {
      if (!uuid(id)) throw new AuthError('INVALID_REQUEST')
      const bytes = await request(token, `works/${id}/content`, undefined, true)
      if (!(bytes instanceof ArrayBuffer) || bytes.byteLength > 33554432) invalid()
      return bytes
    },
    async wallet(token) {
      const x = object(await request(token, 'wallet'))
      if (!['AVAILABLE', 'RESERVED', 'CONSUMED'].includes(x.previewState)) invalid()
      return {
        available: integer(x.available),
        held: integer(x.held),
        previewState: x.previewState,
        generationUnlocked: x.generationUnlocked === undefined ? false : (typeof x.generationUnlocked === 'boolean' ? x.generationUnlocked : invalid()),
        maleIdentityRemaining: x.maleIdentityRemaining === undefined ? 0 : integer(x.maleIdentityRemaining),
        femaleIdentityRemaining: x.femaleIdentityRemaining === undefined ? 0 : integer(x.femaleIdentityRemaining),
      }
    },
    async entriesPage(token, before) {
      if (before !== undefined && before !== null && !/^[1-9][0-9]{0,18}$/.test(String(before))) throw new AuthError('INVALID_REQUEST')
      const page = object(await request(token, 'entries?limit=20' + (before ? '&before=' + encodeURIComponent(before) : '')))
      if (!Array.isArray(page.items) || page.items.length > 20 || page.nextBefore !== null && (typeof page.nextBefore !== 'string' || !/^[1-9][0-9]{0,18}$/.test(page.nextBefore))) invalid()
      return { items: parseRows('entries', page.items), nextBefore: page.nextBefore }
    },
    async list(token, kind) {
      if (!Object.prototype.hasOwnProperty.call(fields, kind))
        throw new AuthError('INVALID_REQUEST')
      const response = await request(token, kind)
      const rows = kind === 'entries' ? object(response).items : response
      if (!Array.isArray(rows) || rows.length > 200) return invalid()
      return parseRows(kind, rows)
    },
    async favorite(token, operationId, templateId, selected) {
      if (!uuid(operationId) || !/^[A-Za-z0-9_-]{1,64}$/.test(templateId))
        throw new AuthError('INVALID_REQUEST')
      const x = object(await request(token, 'favorites', { operationId, templateId, selected }))
      if (x.operationId !== operationId || x.templateId !== templateId || x.selected !== selected)
        invalid()
    },
    async cancel(token, id) {
      if (!uuid(id)) throw new AuthError('INVALID_REQUEST')
      const x = object(await request(token, `jobs/${id}/cancel`, {}))
      if (x.id !== id || x.state !== 'CANCELLED') invalid()
    },
  }
}

export const kindLabel = {
  MODEL: '多视角身份图',
  PREVIEW: '水印小样',
  HD: '高清照片',
  UPSCALE: '巨幅照片',
  RECHARGE: '充值入账',
  PHONE_LINK_IN: '手机号充值关联',
  PHONE_LINK_OUT: '充值关联转出',
  HOLD: '任务预占',
  CAPTURE: '生成消费',
  RELEASE: '释放预占',
}

export const stateLabel = {
  QUEUED: '等待生成',
  RUNNING: '正在生成',
  UNCERTAIN: '结果待核实，请勿重复提交',
  SUCCEEDED: '已完成',
  FAILED: '生成失败，额度已释放',
  CANCELLED: '已取消，额度已释放',
  PENDING: '待支付',
  PAID: '已入账',
  CLOSED: '已关闭',
}

export function businessMessage(error) {
  if (error instanceof AuthError && error.code === 'CANCELLED') return ''
  if (error instanceof AuthError && error.status === 401) return '登录已失效，请重新登录。'
  return '业务服务暂不可用，请重试。写入结果不确定时，请使用原操作重试。'
}

// The server decides when a running identity attempt can be superseded.
export function canRetryIdentity(job) {
  return !!job && job.kind === 'MODEL' && (['FAILED', 'CANCELLED', 'UNCERTAIN'].includes(job.state) || job.retryAllowed === true)
}
export function blocksIdentity(job) {
  return !!job && ['QUEUED', 'RUNNING', 'UNCERTAIN'].includes(job.state) && !job.retryReleased && !canRetryIdentity(job)
}

// Wedding tasks awaiting confirmation must remain visible while they block another submission.
export function visibleGenerationJob(job) {
  return !(job.kind === 'MODEL' && (['FAILED', 'UNCERTAIN'].includes(job.state) || ['QUEUED', 'RUNNING'].includes(job.state) && (job.retryAllowed || job.retryReleased)))
}
